import React, { useMemo } from "react";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { AccessTimeOutlined, CloseRounded } from "@mui/icons-material";

// ─── Time slot generation (06:00 - 21:45 in 15-min increments) ─
const TIME_SLOTS: string[] = (() => {
  const slots: string[] = [];
  for (let h = 6; h < 22; h++) {
    for (let m = 0; m < 60; m += 15) {
      slots.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return slots;
})();

interface ScheduleTimePickerProps {
  /** Date string in YYYY-MM-DD format — time picker only shows when a date is set */
  dateValue: string;
  /** Currently selected time in HH:mm format, or "" */
  timeValue: string;
  /** Called with the selected time in HH:mm format */
  onTimeChange: (time: string) => void;
  /** Whether the time picker dropdown is visible */
  showPicker: boolean;
  /** Toggle time picker visibility */
  onTogglePicker: (show: boolean) => void;
  /** Estimated duration in minutes (for auto-calculated end time) */
  estimatedDurationMinutes?: number;
  /** Optional className for container */
  className?: string;
}

/**
 * Progressive disclosure time picker:
 * - Shows "+ Add start time" link when a date is set but no time selected
 * - Shows time dropdown (15-min increments) with clear button when time is selected
 * - Shows auto-calculated end time when both time and duration are set
 */
const ScheduleTimePicker: React.FC<ScheduleTimePickerProps> = ({
  dateValue,
  timeValue,
  onTimeChange,
  showPicker,
  onTogglePicker,
  estimatedDurationMinutes,
  className,
}) => {
  const { translate } = useTranslation("operations");
  // Auto-calculate end time
  const calculatedEndTime = useMemo(() => {
    if (!dateValue || !timeValue || !estimatedDurationMinutes) return null;
    const [h, m] = timeValue.split(":").map(Number);
    const startMinutes = h * 60 + m;
    const endMinutes = startMinutes + estimatedDurationMinutes;
    const endH = Math.floor(endMinutes / 60) % 24;
    const endM = endMinutes % 60;
    return `${String(endH).padStart(2, "0")}:${String(endM).padStart(2, "0")}`;
  }, [dateValue, timeValue, estimatedDurationMinutes]);

  // Don't render anything if no date is set
  if (!dateValue) return null;

  return (
    <div className={className}>
      {/* "+ Add start time" link — shown when no time is selected and picker is not open */}
      {!timeValue && !showPicker && (
        <button
          type="button"
          onClick={() => onTogglePicker(true)}
          className="flex items-center gap-1.5 mt-1.5 text-[12px] font-medium text-[#D4572B] hover:text-[#B84A24] transition-colors"
          style={{ fontFamily: "'Sora', sans-serif" }}
        >
          <AccessTimeOutlined sx={{ fontSize: 14 }} />
          + Add start time
        </button>
      )}

      {/* Time picker dropdown — shown when picker is open or time is selected */}
      {(showPicker || timeValue) && (
        <div className="flex items-center gap-2 mt-1.5">
          <div className="relative flex-1 max-w-[160px]">
            <select
              value={timeValue}
              onChange={(e) => {
                onTimeChange(e.target.value);
                onTogglePicker(false);
              }}
              className="w-full h-[36px] px-3 pr-8 rounded-lg border-[1.5px] border-[#E8E5DF] bg-[#F7F5F0] text-[13px] font-medium text-[#0C1117] appearance-none cursor-pointer focus:border-[#D4572B] focus:outline-none transition-colors"
              style={{ fontFamily: "'Sora', sans-serif" }}
            >
              <option value="" disabled>
                {translate("timePicker.selectTime")}
              </option>
              {TIME_SLOTS.map((slot) => (
                <option key={slot} value={slot}>
                  {slot}
                </option>
              ))}
            </select>
            <AccessTimeOutlined
              sx={{
                fontSize: 14,
                color: "#8C9099",
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
              }}
            />
          </div>

          {/* Clear button */}
          {timeValue && (
            <button
              type="button"
              onClick={() => {
                onTimeChange("");
                onTogglePicker(false);
              }}
              className="w-[28px] h-[28px] flex items-center justify-center rounded-md bg-[#F2F0EB] hover:bg-[#E8E5DF] transition-colors"
              title={translate("timePicker.removeTime")}
            >
              <CloseRounded sx={{ fontSize: 14, color: "#8C9099" }} />
            </button>
          )}
        </div>
      )}

      {/* Auto-calculated end time */}
      {calculatedEndTime && (
        <div className="flex items-center gap-2 mt-2">
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#EFF6FF] text-[10px] font-semibold text-[#3B82F6] tracking-wide"
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
          >
            AUTO
          </span>
          <span
            className="text-[12px] font-medium text-[#8C9099]"
            style={{ fontFamily: "'Sora', sans-serif" }}
          >
            Ends at {calculatedEndTime}
          </span>
        </div>
      )}
    </div>
  );
};

export default ScheduleTimePicker;
